import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  Tooltip,
  CircularProgress,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Scale,
  Add,
  Refresh,
  Delete,
  Visibility,
  VisibilityOff,
  ContentCopy,
  Key,
  CheckCircle,
  Cancel,
  Router,
  SignalWifi4Bar,
  SignalWifiOff,
} from '@mui/icons-material';
import api from '../../services/api';

// Types
interface ScaleDevice {
  id: string;
  device_code: string;
  name: string;
  laboratory: string;
  location?: string;
  scale_model?: string;
  serial_number?: string;
  is_active: boolean;
  last_seen_at?: string;
  total_readings: number;
  metadata?: {
    raspberry_pi_id?: string;
    firmware_version?: string;
    calibration_date?: string;
    notes?: string;
  };
  created_at: string;
  updated_at: string;
}

interface RegisterDeviceForm {
  device_code: string;
  name: string;
  laboratory: string;
  location: string;
  scale_model: string;
  serial_number: string;
}

const LABORATORY_OPTIONS = [
  { value: 'AU', label: 'AU (Or)', color: '#f59e0b' },
  { value: 'SAA', label: 'SAA (Absorption Atomique)', color: '#3b82f6' },
  { value: 'ICP', label: 'ICP', color: '#8b5cf6' },
  { value: 'VOLUMETRIE', label: 'Volumétrie', color: '#ec4899' },
  { value: 'PREPARATION_MECANIQUE', label: 'Préparation Mécanique', color: '#10b981' },
];

const ScalesManagement: React.FC = () => {
  const [devices, setDevices] = useState<ScaleDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  
  // Form and selection states
  const [selectedDevice, setSelectedDevice] = useState<ScaleDevice | null>(null);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [registerForm, setRegisterForm] = useState<RegisterDeviceForm>({
    device_code: '',
    name: '',
    laboratory: 'PREPARATION_MECANIQUE',
    location: '',
    scale_model: 'OHAUS Explorer',
    serial_number: '',
  });
  const [registerLoading, setRegisterLoading] = useState(false);

  useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      setLoading(true);
      setError(null);
      // api.get returns the data directly, not wrapped in { data: ... }
      const data = await api.get<ScaleDevice[]>('/scale/devices');
      // Ensure we always have an array
      setDevices(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Error loading scale devices:', err);
      setError(err.message || 'Erreur lors du chargement des balances');
      // Reset to empty array on error
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterDevice = async () => {
    try {
      setRegisterLoading(true);
      setError(null);
      
      // api.post returns data directly, not wrapped in { data: ... }
      const data = await api.post<{ success: boolean; api_key: string; device_code: string; message: string }>('/scale/devices/register', registerForm);
      
      // Show the API key
      setNewApiKey(data.api_key);
      setApiKeyDialogOpen(true);
      setRegisterDialogOpen(false);
      
      // Reset form
      setRegisterForm({
        device_code: '',
        name: '',
        laboratory: 'PREPARATION_MECANIQUE',
        location: '',
        scale_model: 'OHAUS Explorer',
        serial_number: '',
      });
      
      // Reload devices
      loadDevices();
      setSuccess('Balance enregistrée avec succès!');
      
    } catch (err: any) {
      console.error('Error registering device:', err);
      setError(err.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleRegenerateApiKey = async (deviceCode: string) => {
    try {
      setError(null);
      // api.post returns data directly, not wrapped in { data: ... }
      const data = await api.post<{ api_key: string }>(`/scale/devices/${deviceCode}/regenerate-key`);
      setNewApiKey(data.api_key);
      setApiKeyDialogOpen(true);
      setSuccess('Nouvelle clé API générée!');
    } catch (err: any) {
      console.error('Error regenerating API key:', err);
      setError(err.message || 'Erreur lors de la régénération de la clé');
    }
  };

  const handleDeactivateDevice = async () => {
    if (!selectedDevice) return;
    
    try {
      setError(null);
      await api.delete(`/scale/devices/${selectedDevice.device_code}`);
      setDeleteDialogOpen(false);
      setSelectedDevice(null);
      loadDevices();
      setSuccess('Balance désactivée avec succès');
    } catch (err: any) {
      console.error('Error deactivating device:', err);
      setError(err.message || 'Erreur lors de la désactivation');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copié dans le presse-papiers!');
    setTimeout(() => setSuccess(null), 2000);
  };

  const getLabColor = (lab: string) => {
    const option = LABORATORY_OPTIONS.find(o => o.value === lab);
    return option?.color || '#6b7280';
  };

  const getLabLabel = (lab: string) => {
    const option = LABORATORY_OPTIONS.find(o => o.value === lab);
    return option?.label || lab;
  };

  const isDeviceOnline = (lastSeen?: string) => {
    if (!lastSeen) return false;
    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const diffMinutes = (now.getTime() - lastSeenDate.getTime()) / (1000 * 60);
    return diffMinutes < 5; // Online if seen in last 5 minutes
  };

  const formatLastSeen = (lastSeen?: string) => {
    if (!lastSeen) return 'Jamais';
    const date = new Date(lastSeen);
    return date.toLocaleString('fr-FR');
  };

  // Statistics - with safety checks
  const activeDevices = (devices || []).filter(d => d.is_active).length;
  const onlineDevices = (devices || []).filter(d => d.is_active && isDeviceOnline(d.last_seen_at)).length;
  const totalReadings = (devices || []).reduce((sum, d) => sum + (d.total_readings || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Scale color="primary" sx={{ fontSize: 40 }} />
          Gestion des Balances
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadDevices}
            disabled={loading}
          >
            Actualiser
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setRegisterDialogOpen(true)}
            sx={{
              background: 'linear-gradient(45deg, #10b981, #059669)',
              '&:hover': { background: 'linear-gradient(45deg, #059669, #047857)' }
            }}
          >
            Nouvelle Balance
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Scale sx={{ fontSize: 40, color: '#10b981' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Balances Actives
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="#059669">
                    {activeDevices}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SignalWifi4Bar sx={{ fontSize: 40, color: '#3b82f6' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    En Ligne
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="#2563eb">
                    {onlineDevices}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fdf4ff, #fae8ff)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Router sx={{ fontSize: 40, color: '#a855f7' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Appareils
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="#9333ea">
                    {(devices || []).length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fffbeb, #fef3c7)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle sx={{ fontSize: 40, color: '#f59e0b' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Pesées
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="#d97706">
                    {totalReadings.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Devices Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Scale />
            Liste des Balances Enregistrées
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (devices || []).length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Aucune balance enregistrée. Cliquez sur "Nouvelle Balance" pour en ajouter une.
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Statut</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Code Appareil</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Nom</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Laboratoire</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Modèle</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Dernière Activité</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Pesées</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(devices || []).map((device) => (
                    <TableRow 
                      key={device.id}
                      sx={{ 
                        '&:hover': { bgcolor: '#f8fafc' },
                        opacity: device.is_active ? 1 : 0.5
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {device.is_active ? (
                            isDeviceOnline(device.last_seen_at) ? (
                              <Tooltip title="En ligne">
                                <SignalWifi4Bar sx={{ color: '#10b981' }} />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Hors ligne">
                                <SignalWifiOff sx={{ color: '#f59e0b' }} />
                              </Tooltip>
                            )
                          ) : (
                            <Tooltip title="Désactivé">
                              <Cancel sx={{ color: '#ef4444' }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                          {device.device_code}
                        </Typography>
                      </TableCell>
                      <TableCell>{device.name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={getLabLabel(device.laboratory)}
                          size="small"
                          sx={{ 
                            bgcolor: getLabColor(device.laboratory),
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {device.scale_model || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatLastSeen(device.last_seen_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={device.total_readings.toLocaleString()}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Voir détails">
                            <IconButton 
                              size="small"
                              onClick={() => {
                                setSelectedDevice(device);
                                setDetailsDialogOpen(true);
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Régénérer clé API">
                            <IconButton 
                              size="small"
                              color="warning"
                              onClick={() => handleRegenerateApiKey(device.device_code)}
                              disabled={!device.is_active}
                            >
                              <Key />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Désactiver">
                            <IconButton 
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedDevice(device);
                                setDeleteDialogOpen(true);
                              }}
                              disabled={!device.is_active}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Register Device Dialog */}
      <Dialog 
        open={registerDialogOpen} 
        onClose={() => setRegisterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Add color="primary" />
          Enregistrer une Nouvelle Balance
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              <Typography variant="body2">
                Après l'enregistrement, une <strong>clé API</strong> sera générée. 
                Cette clé doit être configurée sur le Raspberry Pi de la balance.
                <br/><strong>IMPORTANT:</strong> La clé ne sera affichée qu'une seule fois!
              </Typography>
            </Alert>
            
            <TextField
              label="Code Appareil"
              value={registerForm.device_code}
              onChange={(e) => setRegisterForm({...registerForm, device_code: e.target.value.toUpperCase()})}
              placeholder="SCALE-PM-001"
              fullWidth
              required
              helperText="Identifiant unique (ex: SCALE-PM-001)"
            />
            
            <TextField
              label="Nom de la Balance"
              value={registerForm.name}
              onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
              placeholder="OHAUS Scale - Préparation Mécanique"
              fullWidth
              required
            />
            
            <FormControl fullWidth required>
              <InputLabel>Laboratoire</InputLabel>
              <Select
                value={registerForm.laboratory}
                onChange={(e) => setRegisterForm({...registerForm, laboratory: e.target.value})}
                label="Laboratoire"
              >
                {LABORATORY_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box 
                        sx={{ 
                          width: 12, 
                          height: 12, 
                          borderRadius: '50%', 
                          bgcolor: option.color 
                        }} 
                      />
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label="Emplacement"
              value={registerForm.location}
              onChange={(e) => setRegisterForm({...registerForm, location: e.target.value})}
              placeholder="Atelier Préparation Mécanique - Station 1"
              fullWidth
            />
            
            <TextField
              label="Modèle de Balance"
              value={registerForm.scale_model}
              onChange={(e) => setRegisterForm({...registerForm, scale_model: e.target.value})}
              placeholder="OHAUS Explorer EX2202"
              fullWidth
            />
            
            <TextField
              label="Numéro de Série"
              value={registerForm.serial_number}
              onChange={(e) => setRegisterForm({...registerForm, serial_number: e.target.value})}
              placeholder="B123456789"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRegisterDialogOpen(false)}>
            Annuler
          </Button>
          <Button 
            variant="contained"
            onClick={handleRegisterDevice}
            disabled={!registerForm.device_code || !registerForm.name || registerLoading}
            startIcon={registerLoading ? <CircularProgress size={20} /> : <Add />}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* API Key Dialog */}
      <Dialog 
        open={apiKeyDialogOpen} 
        onClose={() => {
          setApiKeyDialogOpen(false);
          setNewApiKey(null);
          setShowApiKey(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
          <Key />
          Clé API Générée
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="bold">
                ATTENTION: Cette clé ne sera affichée qu'une seule fois!
              </Typography>
              <Typography variant="body2">
                Copiez-la maintenant et configurez-la sur votre Raspberry Pi.
              </Typography>
            </Alert>
            
            <TextField
              label="Clé API"
              value={newApiKey || ''}
              fullWidth
              type={showApiKey ? 'text' : 'password'}
              InputProps={{
                readOnly: true,
                sx: { fontFamily: 'monospace', fontSize: '0.9rem' },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowApiKey(!showApiKey)}>
                      {showApiKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                    <IconButton onClick={() => newApiKey && copyToClipboard(newApiKey)}>
                      <ContentCopy />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="subtitle2" gutterBottom>
              Configuration Raspberry Pi:
            </Typography>
            <Paper sx={{ p: 2, bgcolor: '#1e293b', color: '#e2e8f0' }}>
              <Typography 
                variant="body2" 
                component="pre" 
                sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}
              >
{`# config.py
API_KEY = "${showApiKey ? newApiKey : '**********************'}"
SERVER_URL = "http://your-server:4000"
LABORATORY = "PREPARATION_MECANIQUE"

# Headers for API calls
headers = {
    "X-Scale-API-Key": API_KEY,
    "Content-Type": "application/json"
}`}
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            variant="contained" 
            onClick={() => {
              setApiKeyDialogOpen(false);
              setNewApiKey(null);
              setShowApiKey(false);
            }}
          >
            J'ai copié la clé
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Désactiver la Balance
        </DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir désactiver la balance <strong>{selectedDevice?.device_code}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            La balance ne pourra plus envoyer de données jusqu'à sa réactivation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Annuler
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeactivateDevice}
          >
            Désactiver
          </Button>
        </DialogActions>
      </Dialog>

      {/* Device Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Scale color="primary" />
          Détails de la Balance
        </DialogTitle>
        <DialogContent>
          {selectedDevice && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Code Appareil</Typography>
                  <Typography fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                    {selectedDevice.device_code}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Statut</Typography>
                  <Box>
                    <Chip 
                      label={selectedDevice.is_active ? 'Actif' : 'Désactivé'}
                      color={selectedDevice.is_active ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Nom</Typography>
                  <Typography>{selectedDevice.name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Laboratoire</Typography>
                  <Box>
                    <Chip 
                      label={getLabLabel(selectedDevice.laboratory)}
                      size="small"
                      sx={{ 
                        bgcolor: getLabColor(selectedDevice.laboratory),
                        color: 'white'
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Emplacement</Typography>
                  <Typography>{selectedDevice.location || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Modèle</Typography>
                  <Typography>{selectedDevice.scale_model || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">N° Série</Typography>
                  <Typography>{selectedDevice.serial_number || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Total Pesées</Typography>
                  <Typography variant="h6" color="primary">
                    {selectedDevice.total_readings.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Dernière Activité</Typography>
                  <Typography>
                    {formatLastSeen(selectedDevice.last_seen_at)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Date Création</Typography>
                  <Typography variant="body2">
                    {new Date(selectedDevice.created_at).toLocaleDateString('fr-FR')}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Dernière Mise à Jour</Typography>
                  <Typography variant="body2">
                    {new Date(selectedDevice.updated_at).toLocaleDateString('fr-FR')}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScalesManagement;
